import { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";

type DropZoneProps = {
  title: string;
  description: string;
  buttonLabel: string;
  hint: string;
  accept: string;
  multiple?: boolean;
  /** Recebe só os arquivos que passam no filtro de tipo */
  onFiles: (files: File[]) => void;
  isValid: (file: File) => boolean;
  invalidMessage: string;
  onInvalid: (message: string) => void;
};

/** Área de envio no estilo iLovePDF: botão grande e arrastar/soltar. */
export const DropZone = ({
  title,
  description,
  buttonLabel,
  hint,
  accept,
  multiple,
  onFiles,
  isValid,
  invalidMessage,
  onInvalid,
}: DropZoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handle = (list: FileList | null) => {
    const files = Array.from(list ?? []);
    const valid = files.filter(isValid);

    if (valid.length !== files.length) onInvalid(invalidMessage);
    if (valid.length > 0) onFiles(multiple ? valid : valid.slice(0, 1));
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        handle(e.dataTransfer.files);
      }}
      className={cn(
        "flex flex-col items-center gap-4 rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors",
        dragging ? "border-primary bg-primary/5" : "border-muted-foreground/30 bg-muted/20"
      )}
    >
      <div className="space-y-2">
        <h2 className="text-2xl font-bold">{title}</h2>
        <p className="mx-auto max-w-xl text-muted-foreground">{description}</p>
      </div>

      <Button
        type="button"
        size="lg"
        className={cn("h-14 px-10 text-base", colors.bg_primary)}
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="mr-2 h-5 w-5" />
        {buttonLabel}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          handle(e.target.files);
          e.target.value = "";
        }}
      />
      <p className="text-sm text-muted-foreground">{hint}</p>
    </div>
  );
};
